import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Home, LogOut, Plus, Play, Square, Download, Copy, Users, CheckCircle, XCircle, Trash2, Camera, ScanLine, Volume2, VolumeX } from 'lucide-react';
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import API_URL from '../../config';
import '../Home.css';
import './FacultyHome.css';

const ScannerAttendance = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [view, setView] = useState('classes'); // classes | create | session_start | live
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Scanner state
    const [showCamera, setShowCamera] = useState(false);
    const [scannerStatus, setScannerStatus] = useState('idle'); // idle | scanning | processing | success | error
    const [lastScanned, setLastScanned] = useState(null);
    const [enableSounds, setEnableSounds] = useState(true);
    const [isTorchOn, setIsTorchOn] = useState(false);
    const [hasTorch, setHasTorch] = useState(false);
    const scannerRef = useRef(null);
    const scannerId = "qr-reader-live";

    // VJS-Style optimized scanning state
    const lastProcessedCodeRef = useRef(null);
    const sameQrHoldStartRef = useRef(null);
    const isScanningRef = useRef(false);
    const audioContextRef = useRef(null);

    // Create class state
    const [newClassName, setNewClassName] = useState('');
    const [newClassCategory, setNewClassCategory] = useState('');
    const [memberText, setMemberText] = useState('');

    // Session state
    const [selectedClass, setSelectedClass] = useState(null);
    const [sessionType, setSessionType] = useState('Morning');
    const [activeSession, setActiveSession] = useState(null);
    const [report, setReport] = useState(null);
    const pollRef = useRef(null);

    // History
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);

    // Copied feedback
    const [copied, setCopied] = useState(false);

    // Audio feedback (Matches VJS Scanner Premium Tones)
    const playSound = useCallback(async (type) => {
        if (!enableSounds) return;
        try {
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const context = audioContextRef.current;
            if (context.state === 'suspended') await context.resume();

            const osc = context.createOscillator();
            const gain = context.createGain();
            osc.connect(gain);
            gain.connect(context.destination);

            if (type === 'valid') {
                // Success: Two-tone rising (C5 -> E5)
                osc.frequency.setValueAtTime(523.25, context.currentTime); 
                osc.frequency.setValueAtTime(659.25, context.currentTime + 0.1);
                gain.gain.setValueAtTime(0.3, context.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.3);
                osc.start();
                osc.stop(context.currentTime + 0.3);
            } else if (type === 'duplicate') {
                // Warning: Mid-tone beep
                osc.frequency.setValueAtTime(440, context.currentTime);
                gain.gain.setValueAtTime(0.2, context.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.25);
                osc.start();
                osc.stop(context.currentTime + 0.25);
            } else { // error
                // Error: Low buzzer
                osc.frequency.setValueAtTime(200, context.currentTime);
                gain.gain.setValueAtTime(0.2, context.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.2);
                osc.start();
                osc.stop(context.currentTime + 0.2);
            }
        } catch (err) { console.error('Audio error:', err); }
    }, [enableSounds]);

    useEffect(() => {
        return () => {
            if (audioContextRef.current) audioContextRef.current.close();
        };
    }, []);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
        if (!userData.email || (userData.role !== "faculty" && userData.role !== "admin")) {
            navigate("/");
            return;
        }
        setUser(userData);
        fetchClasses();
    }, [navigate]);

    const fetchClasses = async () => {
        try {
            const res = await fetch(`${API_URL}/scanner-attendance/classes`);
            const data = await res.json();
            if (data.ok) setClasses(data.classes || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const createClass = async () => {
        if (!newClassName.trim()) return alert('Class name is required');
        const lines = memberText.trim().split('\n').filter(l => l.trim());
        if (lines.length === 0) return alert('Add at least one student');

        const members = lines.map(line => {
            const parts = line.split(',').map(p => p.trim());
            return { roll: parts[0], name: parts[1] || '' };
        });

        try {
            const res = await fetch(`${API_URL}/scanner-attendance/classes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newClassName.trim(),
                    category: newClassCategory.trim(),
                    created_by: user.email,
                    members
                })
            });
            const data = await res.json();
            if (data.ok) {
                setNewClassName(''); setNewClassCategory(''); setMemberText('');
                setView('classes');
                fetchClasses();
            } else alert(data.error || 'Failed');
        } catch (err) { alert('Network error'); }
    };

    const deleteClass = async (id) => {
        if (!window.confirm('Delete this class? This cannot be undone.')) return;
        try {
            const res = await fetch(`${API_URL}/scanner-attendance/classes/${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.ok) fetchClasses();
            else alert(data.error || 'Failed');
        } catch (err) { alert('Network error'); }
    };

    const startSession = async () => {
        try {
            const res = await fetch(`${API_URL}/scanner-attendance/sessions/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    class_id: selectedClass.id,
                    faculty_id: user.email,
                    session_type: sessionType
                })
            });
            const data = await res.json();
            if (data.ok) {
                setActiveSession({ id: data.session_id, date: data.date });
                setView('live');
                startPolling(data.session_id);
            } else alert(data.error || 'Failed');
        } catch (err) { alert('Network error'); }
    };

    const handleScanResult = async (text) => {
        // 1. Cooldown / Processing check
        if (isScanningRef.current) return;

        // 2. Same-QR suppression (VJS Logic)
        const nowMs = Date.now();
        if (lastProcessedCodeRef.current && text === lastProcessedCodeRef.current) {
            if (!sameQrHoldStartRef.current) sameQrHoldStartRef.current = nowMs;
            const heldFor = nowMs - sameQrHoldStartRef.current;
            if (heldFor >= 3000) {
                setScannerStatus('idle');
            }
            return;
        } else {
            sameQrHoldStartRef.current = null;
        }

        // 3. Mark as processing
        lastProcessedCodeRef.current = text;
        isScanningRef.current = true;
        setScannerStatus('processing');
        
        // 4. Extract roll
        const roll = text.includes('|') ? text.split('|')[0].trim() : text.trim();
        
        // 5. Play sound immediately for zero-lag feel
        playSound('valid');

        // 6. Non-blocking upload
        fetch(`${API_URL}/scanner-attendance/sessions/${activeSession.id}/scan`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roll })
        }).then(res => res.json()).then(data => {
            setLastScanned({
                roll: roll,
                status: data.status,
                message: data.message,
                time: new Date().toLocaleTimeString()
            });

            if (data.status === 'PRESENT') {
                setScannerStatus('success');
                // Auto-refresh stats in background
                setTimeout(() => fetchReport(activeSession.id), 100);
            } else {
                setScannerStatus('error');
                playSound('invalid');
            }
        }).catch(err => {
            console.error('Upload error:', err);
            setScannerStatus('error');
        }).finally(() => {
            // Short cooldown for AFAP scanning
            setTimeout(() => {
                isScanningRef.current = false;
                setScannerStatus('scanning');
            }, 300);
        });
    };

    const startScanner = async () => {
        if (!activeSession) return;
        setShowCamera(true);
        setScannerStatus('scanning');
        lastProcessedCodeRef.current = null;
        isScanningRef.current = false;
        
        setTimeout(() => {
            try {
                // Calculate qrbox size exactly like VJS Scanner
                const viewportWidth = Math.min(window.innerWidth - 40, 360);
                const qrboxWidth = Math.min(viewportWidth * 0.85, 300);
                const qrboxHeight = qrboxWidth * 0.5; // 2:1 for barcodes

                const scanner = new Html5QrcodeScanner(
                    scannerId,
                    { 
                        fps: 30, // Increased FPS for faster scanning
                        qrbox: { width: qrboxWidth, height: qrboxHeight },
                        formatsToSupport: [
                            Html5QrcodeSupportedFormats.QR_CODE,
                            Html5QrcodeSupportedFormats.CODE_128,
                            Html5QrcodeSupportedFormats.CODE_39,
                            Html5QrcodeSupportedFormats.EAN_13,
                            Html5QrcodeSupportedFormats.EAN_8,
                            Html5QrcodeSupportedFormats.UPC_A,
                            Html5QrcodeSupportedFormats.UPC_E,
                        ],
                        aspectRatio: 1.777778, // 16:9
                        disableFlip: false, // Allow flipped scanning
                        rememberLastUsedCamera: true,
                        showTorchButtonIfSupported: true, // Native torch button
                    },
                    false // verbose
                );
                
                scannerRef.current = scanner;

                scanner.render(
                    (decodedText) => {
                        handleScanResult(decodedText);
                    },
                    (errorMessage) => {
                        // Ignore standard scanning errors
                    }
                );
            } catch (err) {
                console.error('Scanner init error:', err);
                alert('Could not start scanner.');
                setShowCamera(false);
            }
        }, 100);
    };

    const stopScanner = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.clear();
                scannerRef.current = null;
            } catch (err) { console.error('Stop error:', err); }
        }
        setShowCamera(false);
        setScannerStatus('idle');
    };


    useEffect(() => {
        return () => {
            if (scannerRef.current) {
                scannerRef.current.clear().catch(() => {});
            }
        };
    }, []);

    const fetchReport = useCallback(async (sessionId) => {
        try {
            const res = await fetch(`${API_URL}/scanner-attendance/sessions/${sessionId}/report`);
            const data = await res.json();
            if (data.ok) setReport(data);
        } catch (err) { console.error(err); }
    }, []);

    const startPolling = useCallback((sessionId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        fetchReport(sessionId);
        pollRef.current = setInterval(() => fetchReport(sessionId), 3000);
    }, [fetchReport]);

    useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

    const closeSession = async () => {
        if (!activeSession) return;
        if (!window.confirm('Close this session? No more scans will be accepted.')) return;
        try {
            await fetch(`${API_URL}/scanner-attendance/sessions/${activeSession.id}/close`, { method: 'POST' });
            if (pollRef.current) clearInterval(pollRef.current);
            fetchReport(activeSession.id);
        } catch (err) { alert('Failed to close'); }
    };

    const saveAndEnd = async () => {
        if (!activeSession) return;
        if (!window.confirm('Save and end this session?')) return;
        try {
            await fetch(`${API_URL}/scanner-attendance/sessions/${activeSession.id}/close`, { method: 'POST' });
            if (pollRef.current) clearInterval(pollRef.current);
            setActiveSession(null); setReport(null); setView('classes');
            fetchClasses();
        } catch (err) { alert('Failed'); }
    };

    const exportExcel = () => {
        if (!activeSession) return;
        const url = `${API_URL}/scanner-attendance/sessions/${activeSession.id}/report?format=excel&exporter_email=${encodeURIComponent(user.email)}`;
        window.open(url, '_blank');
    };

    const copyScanUrl = () => {
        if (!activeSession) return;
        const baseUrl = API_URL || window.location.origin;
        const url = `${baseUrl}/scanner-attendance/sessions/${activeSession.id}/scan`;
        navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    };

    const fetchHistory = async () => {
        try {
            const res = await fetch(`${API_URL}/scanner-attendance/sessions/history?limit=30`);
            const data = await res.json();
            if (data.ok) setHistory(data.sessions || []);
        } catch (err) { console.error(err); }
    };

    const viewPastSession = async (session) => {
        setActiveSession({ id: session.id, date: session.date });
        setSelectedClass({ id: session.class_id, name: session.class_name });
        setView('live');
        fetchReport(session.id);
    };

    // ============ STYLES ============
    const cardStyle = {
        background: 'var(--glass-bg)', border: '1px solid var(--glass-border)',
        borderRadius: '16px', padding: '1.5rem', transition: 'all 0.2s',
        backdropFilter: 'blur(12px)'
    };
    const btnPrimary = {
        background: 'var(--accent-color)', color: '#fff', border: 'none',
        padding: '0.8rem 1.8rem', borderRadius: '12px', cursor: 'pointer',
        fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
        transition: 'transform 0.15s', boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
    };
    const btnSecondary = {
        ...btnPrimary, background: 'rgba(255,255,255,0.08)', boxShadow: 'none',
        border: '1px solid var(--glass-border)'
    };
    const btnDanger = { ...btnPrimary, background: '#ef4444', boxShadow: '0 4px 12px rgba(239,68,68,0.3)' };
    const btnSuccess = { ...btnPrimary, background: '#10b981', boxShadow: '0 4px 12px rgba(16,185,129,0.3)' };
    const inputStyle = {
        width: '100%', padding: '0.9rem 1rem', background: 'var(--input-bg)',
        border: '1px solid var(--input-border)', borderRadius: '10px',
        color: 'var(--input-text)', fontSize: '0.95rem', outline: 'none',
        fontFamily: 'inherit', transition: 'border 0.2s'
    };
    const labelStyle = { fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.4rem', display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' };

    if (loading) return <div style={{ color: 'white', textAlign: 'center', marginTop: '5rem' }}>Loading...</div>;

    return (
        <div className="home-page-container">
            <div className="home-content-wrapper" style={{ maxWidth: '1000px', margin: '0 auto' }}>
                {/* HEADER */}
                <header style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button onClick={() => view === 'classes' ? navigate('/faculty/attendance') : (view === 'live' && report?.session?.status === 'active' ? null : setView('classes'))}
                        className="back-btn-modern" style={{ position: 'static', transform: 'none', flexShrink: 0 }}>
                        <ChevronLeft size={20} /> Back
                    </button>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                        <h1 className="brand-title" style={{ fontSize: '1.8rem', margin: 0 }}>📷 Scanner Attendance</h1>
                        <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>Scan → Mark → Export</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button onClick={() => navigate('/faculty/home')} title="Home" style={{ width: 42, height: 42, borderRadius: '12px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Home size={18} /></button>
                        <button onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }} title="Logout" style={{ width: 42, height: 42, borderRadius: '12px', border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LogOut size={18} /></button>
                    </div>
                </header>

                {/* ========== CLASSES LIST VIEW ========== */}
                {view === 'classes' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {/* Action bar */}
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <button onClick={() => setView('create')} style={btnPrimary}><Plus size={18} /> Create Class</button>
                            <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHistory(); }} style={btnSecondary}>
                                {showHistory ? 'Hide History' : '📜 Past Sessions'}
                            </button>
                        </div>

                        {/* History panel */}
                        {showHistory && (
                            <div style={{ ...cardStyle, borderLeft: '4px solid var(--accent-color)' }}>
                                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: 'var(--text-main)' }}>📜 Past Sessions</h3>
                                {history.length === 0 ? (
                                    <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>No past sessions yet.</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {history.map(s => (
                                            <div key={s.id} onClick={() => viewPastSession(s)} style={{ ...cardStyle, padding: '1rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                <div>
                                                    <div style={{ fontWeight: 700, color: 'var(--text-main)' }}>{s.class_name}</div>
                                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{s.date} • {s.session_type}</div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem' }}>
                                                    <span style={{ color: '#10b981', fontWeight: 700 }}>✓ {s.present_count}</span>
                                                    <span style={{ color: '#ef4444', fontWeight: 700 }}>✗ {s.total_count - s.present_count}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Classes grid */}
                        {classes.length === 0 ? (
                            <div style={{ ...cardStyle, textAlign: 'center', padding: '4rem 2rem' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
                                <div style={{ color: 'var(--text-muted)', fontSize: '1.1rem' }}>No classes yet. Create one to get started.</div>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
                                {classes.map(cls => (
                                    <div key={cls.id} style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-main)' }}>{cls.name}</div>
                                                {cls.category && <div style={{ fontSize: '0.8rem', color: 'var(--accent-color)', marginTop: '0.25rem' }}>{cls.category}</div>}
                                            </div>
                                            <button onClick={(e) => { e.stopPropagation(); deleteClass(cls.id); }} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px', opacity: 0.6 }} title="Delete"><Trash2 size={16} /></button>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                            <Users size={16} /> {cls.member_count} students
                                        </div>
                                        <button onClick={() => { setSelectedClass(cls); setView('session_start'); }}
                                            style={{ ...btnSuccess, width: '100%', justifyContent: 'center' }}>
                                            <Play size={16} /> Start Session
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ========== CREATE CLASS VIEW ========== */}
                {view === 'create' && (
                    <div style={{ ...cardStyle, maxWidth: '600px' }}>
                        <h2 style={{ margin: '0 0 1.5rem 0', fontSize: '1.3rem' }}>➕ Create New Class</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div>
                                <label style={labelStyle}>Class Name *</label>
                                <input value={newClassName} onChange={e => setNewClassName(e.target.value)} placeholder="e.g. SI AIML 25-26" style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>Category (optional)</label>
                                <input value={newClassCategory} onChange={e => setNewClassCategory(e.target.value)} placeholder="e.g. Smart Interviews" style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>Students (one per line: RollNo or RollNo,Name)</label>
                                <textarea value={memberText} onChange={e => setMemberText(e.target.value)}
                                    placeholder={"22A91A0501,Ravi Kumar\n22A91A0502,Priya S\n22A91A0503"}
                                    rows={10} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.6 }} />
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                                    {memberText.trim().split('\n').filter(l => l.trim()).length} students entered
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                <button onClick={createClass} style={btnPrimary}><CheckCircle size={16} /> Save Class</button>
                                <button onClick={() => setView('classes')} style={btnSecondary}><XCircle size={16} /> Cancel</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ========== START SESSION VIEW ========== */}
                {view === 'session_start' && selectedClass && (
                    <div style={{ ...cardStyle, maxWidth: '500px' }}>
                        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>▶ Start Session</h2>
                        <div style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                            <strong>{selectedClass.name}</strong> — {selectedClass.member_count} students
                        </div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={labelStyle}>Session Type</label>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                {['Morning', 'Afternoon'].map(t => (
                                    <button key={t} onClick={() => setSessionType(t)}
                                        style={{ flex: 1, padding: '1rem', borderRadius: '12px', cursor: 'pointer', fontWeight: 700, fontSize: '1rem', border: sessionType === t ? '2px solid var(--accent-color)' : '1px solid var(--glass-border)', background: sessionType === t ? 'rgba(var(--accent-color), 0.15)' : 'var(--glass-bg)', color: sessionType === t ? 'var(--accent-color)' : 'var(--text-secondary)', transition: 'all 0.2s' }}>
                                        {t === 'Morning' ? '🌅' : '🌇'} {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button onClick={startSession} style={btnSuccess}><Play size={16} /> Start Now</button>
                            <button onClick={() => setView('classes')} style={btnSecondary}>Cancel</button>
                        </div>
                    </div>
                )}

                {/* ========== LIVE SESSION VIEW ========== */}
                {view === 'live' && activeSession && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {/* Session header card */}
                        <div style={{ ...cardStyle, borderLeft: `4px solid ${report?.session?.status === 'active' ? '#10b981' : '#6b7280'}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: report?.session?.status === 'active' ? '#10b981' : '#6b7280', display: 'inline-block', animation: report?.session?.status === 'active' ? 'pulse 2s infinite' : 'none' }} />
                                        <span style={{ fontWeight: 700, fontSize: '1.2rem', color: 'var(--text-main)' }}>{report?.session?.class_name || selectedClass?.name}</span>
                                    </div>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                                        {report?.session?.session_type || sessionType} • {report?.session?.date || activeSession.date} • Session #{activeSession.id}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <button onClick={() => setEnableSounds(!enableSounds)} style={{ ...btnSecondary, width: 42, height: 42, padding: 0, borderRadius: '12px', justifyContent: 'center' }} title={enableSounds ? "Mute" : "Unmute"}>
                                        {enableSounds ? <Volume2 size={18} /> : <VolumeX size={18} />}
                                    </button>
                                    {!showCamera ? (
                                        <button onClick={startScanner} style={{ ...btnSuccess, padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
                                            <Camera size={14} /> Open Camera
                                        </button>
                                    ) : (
                                        <button onClick={stopScanner} style={{ ...btnDanger, padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
                                            <Square size={14} /> Close
                                        </button>
                                    )}
                                    <button onClick={copyScanUrl} style={{ ...btnSecondary, padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
                                        <Copy size={14} /> {copied ? '✅ Copied!' : 'Copy API URL'}
                                    </button>
                                    <button onClick={exportExcel} style={{ ...btnPrimary, padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
                                        <Download size={14} /> Export Excel
                                    </button>
                                    {report?.session?.status === 'active' && (
                                        <>
                                            <button onClick={closeSession} style={{ ...btnDanger, padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
                                                <Square size={14} /> Stop Session
                                            </button>
                                            <button onClick={saveAndEnd} style={{ ...btnSuccess, padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
                                                💾 Save & End
                                            </button>
                                        </>
                                    )}
                                    {report?.session?.status === 'closed' && (
                                        <button onClick={() => { setActiveSession(null); setReport(null); setView('classes'); }} style={{ ...btnSecondary, padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
                                            ← Back to Classes
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* SCANNER VIEW */}
                        {showCamera && (
                            <div style={{ ...cardStyle, position: 'relative', overflow: 'hidden', padding: 0, minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                                <div id={scannerId} style={{ width: '100%', maxWidth: '500px' }}></div>
                                
                                {/* Overlay status */}
                                {scannerStatus !== 'scanning' && scannerStatus !== 'idle' && (
                                    <div style={{
                                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                        background: scannerStatus === 'success' ? 'rgba(16,185,129,0.9)' : (scannerStatus === 'error' ? 'rgba(239,68,68,0.9)' : 'rgba(0,0,0,0.7)'),
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, color: '#fff', textAlign: 'center', padding: '2rem'
                                    }}>
                                        {scannerStatus === 'processing' ? (
                                            <div className="scanner-pulse" style={{ fontSize: '1.2rem', fontWeight: 700 }}>Processing...</div>
                                        ) : scannerStatus === 'success' ? (
                                            <>
                                                <CheckCircle size={64} style={{ marginBottom: '1rem' }} />
                                                <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{lastScanned?.roll}</div>
                                                <div style={{ fontSize: '1rem' }}>MARKED PRESENT ✅</div>
                                            </>
                                        ) : (
                                            <>
                                                <XCircle size={64} style={{ marginBottom: '1rem' }} />
                                                <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{lastScanned?.roll || 'ERROR'}</div>
                                                <div style={{ fontSize: '1rem' }}>{lastScanned?.message || 'SCAN FAILED'}</div>
                                            </>
                                        )}
                                    </div>
                                )}
                                
                                <div style={{ position: 'absolute', bottom: '1rem', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '0.4rem 1rem', borderRadius: '20px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem', zIndex: 5 }}>
                                    <ScanLine size={14} className="scanner-pulse" /> Scanner Active
                                </div>
                            </div>
                        )}

                        {/* Stats bar */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                            {[
                                { label: 'Present', value: report?.present_count ?? 0, color: '#10b981', icon: '✅' },
                                { label: 'Absent', value: report?.absent_count ?? 0, color: '#ef4444', icon: '❌' },
                                { label: 'Total', value: report?.total ?? 0, color: 'var(--accent-color)', icon: '👥' }
                            ].map(s => (
                                <div key={s.label} style={{ ...cardStyle, textAlign: 'center', padding: '1.2rem' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: 800, color: s.color }}>{s.icon} {s.value}</div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.3rem', fontWeight: 600 }}>{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Present list */}
                        <div style={cardStyle}>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <CheckCircle size={20} /> Present ({report?.present?.length ?? 0})
                            </h3>
                            {(!report?.present || report.present.length === 0) ? (
                                <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Waiting for scans...</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '300px', overflowY: 'auto' }}>
                                    {report.present.map((p, i) => (
                                        <div key={p.roll_no} style={{ display: 'flex', alignItems: 'center', padding: '0.6rem 0.8rem', borderRadius: '8px', background: i % 2 === 0 ? 'rgba(16,185,129,0.05)' : 'transparent', gap: '1rem' }}>
                                            <span style={{ fontWeight: 700, color: 'var(--accent-color)', width: '120px', flexShrink: 0, fontSize: '0.9rem' }}>{p.roll_no}</span>
                                            <span style={{ flex: 1, color: 'var(--text-main)', fontWeight: 500 }}>{p.name || '—'}</span>
                                            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', flexShrink: 0 }}>
                                                {p.time ? new Date(p.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Absent list */}
                        <div style={cardStyle}>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <XCircle size={20} /> Absent ({report?.absent?.length ?? 0})
                            </h3>
                            {(!report?.absent || report.absent.length === 0) ? (
                                <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>Everyone's here! 🎉</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '250px', overflowY: 'auto' }}>
                                    {report.absent.map((a, i) => (
                                        <div key={a.roll_no} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0.8rem', borderRadius: '8px', background: i % 2 === 0 ? 'rgba(239,68,68,0.04)' : 'transparent', gap: '1rem' }}>
                                            <span style={{ fontWeight: 700, color: 'var(--text-secondary)', width: '120px', flexShrink: 0, fontSize: '0.9rem' }}>{a.roll_no}</span>
                                            <span style={{ flex: 1, color: 'var(--text-muted)' }}>{a.name || '—'}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Pulse animation */}
                <style>{`
                    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
                    .scanner-pulse { animation: pulse 1.5s infinite ease-in-out; }
                    
                    /* Override html5-qrcode fullscreen mobile styles to match VJS */
                    #qr-reader-live {
                      max-width: 100% !important;
                      width: 100% !important;
                      border: none !important;
                      background: #000;
                    }
                    #qr-reader-live video {
                      max-height: 280px !important;
                      object-fit: cover !important;
                    }
                    #qr-reader-live__dashboard_section_swaplink {
                      display: none !important;
                    }
                    #qr-reader-live__dashboard_section_csr {
                      max-height: 280px !important;
                    }
                    #qr-reader-live__scan_region {
                      max-height: 280px !important;
                      background: #000;
                    }
                    /* VJS UI overrides */
                    #qr-reader-live__header_message {
                      font-size: 12px !important;
                      padding: 4px !important;
                      color: #fff !important;
                    }
                    #qr-reader-live select {
                      font-size: 13px !important;
                      padding: 6px !important;
                      margin-bottom: 10px !important;
                    }
                    #qr-reader-live button {
                        background: var(--accent-color) !important; 
                        color: white !important; 
                        border: none !important; 
                        padding: 0.5rem 1rem !important; 
                        border-radius: 8px !important;
                        cursor: pointer !important;
                        font-size: 13px !important;
                        font-weight: bold;
                    }
                `}</style>
            </div>
        </div>
    );
};

export default ScannerAttendance;
